/**
 * AI Provider Types
 * 
 * This module defines the common interfaces for AI provider implementations.
 * All providers (OpenAI, Google Gemini, etc.) must implement these interfaces.
 */

/** Supported AI provider types */
export type AIProviderType = 'openai' | 'google';

/** Configuration for AI providers */
export interface AIProviderConfig {
  model: string;
  temperature: number;
  maxTokens: number;
}

/** Chat message format (provider-agnostic) */
export interface ChatMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

/** Options for chat completion requests */
export interface ChatCompletionOptions {
  /** Whether to request JSON-formatted output */
  jsonMode?: boolean;
}

/** Token usage information */
export interface TokenUsage {
  /** Number of tokens in the prompt/input */
  promptTokens: number;
  /** Number of tokens in the completion/output */
  completionTokens: number;
  /** Total tokens used */
  totalTokens: number;
}

/** Response from a chat completion request */
export interface ChatCompletionResponse {
  /** The generated text content */
  content: string;
  /** Why the generation stopped (if available) */
  finishReason?: string;
  /** Token usage information (if available) */
  usage?: TokenUsage;
}

/**
 * AI Provider Interface
 * 
 * All AI providers must implement this interface to be compatible
 * with the conversational search system.
 */
export interface AIProvider {
  /** The provider type identifier */
  readonly providerType: AIProviderType;
  
  /**
   * Send a chat completion request to the AI provider
   * 
   * @param messages - Array of chat messages (system, user, assistant)
   * @param options - Optional configuration (e.g., JSON mode)
   * @returns Promise resolving to the completion response
   */
  chat(messages: ChatMessage[], options?: ChatCompletionOptions): Promise<ChatCompletionResponse>;
}

/** Environment variable names for each provider's API key */
export const PROVIDER_ENV_KEYS: Record<AIProviderType, string> = {
  openai: 'OPENAI_API_KEY',
  google: 'GOOGLE_AI_API_KEY',
};

/** Default models for each provider */
export const PROVIDER_DEFAULT_MODELS: Record<AIProviderType, string> = {
  openai: 'gpt-5.4-mini',
  google: 'gemini-3.1-flash-lite',
};

